import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Sparkles, User, LogOut, Home, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Header() {
  const { user, usage, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <motion.header 
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-md bg-black/50 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <Sparkles className="h-6 w-6 text-blue-500" />
          <span className="font-heading font-bold text-xl">MyTestCase</span>
        </Link>

        <div className="flex items-center gap-4">
          {/* Home Button - Visible for all users */}
          <Button
            size="lg"
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 font-semibold px-8 py-6"
            data-testid="home-button"
          >
            <Home className="h-5 w-5 mr-2" />
            Home
          </Button>

          {/* How To Button - Visible for all users */}
          <Button
            size="lg"
            onClick={() => navigate('/how-to')}
            className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 font-semibold px-8 py-6"
            data-testid="how-to-button"
          >
            <HelpCircle className="h-5 w-5 mr-2" />
            How To
          </Button>

          {user ? (
            <>
              <div 
                className="px-4 py-2 bg-secondary/50 rounded-md border border-border"
                data-testid="usage-counter"
              >
                <span className="text-sm text-muted-foreground">Usage: </span>
                <span className="text-sm font-medium">
                  {usage.tier === 'paid' ? 'Unlimited' : `${usage.used}/${usage.limit}`}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/profile')}
                data-testid="profile-button"
              >
                <User className="h-4 w-4 mr-2" />
                Profile
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                size="lg"
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 font-semibold px-8 py-6"
                data-testid="login-button"
              >
                <svg 
                  className="h-5 w-5 mr-2" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" 
                  />
                </svg>
                Login
              </Button>
              {/* Sign Up button - Hidden for now */}
              {/* <Button
                size="sm"
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-blue-600 to-violet-600 hover:opacity-90"
                data-testid="signup-button"
              >
                Sign Up
              </Button> */}
            </>
          )}
        </div>
      </div>
    </motion.header>
  );
}